// Website template options (ARCHITECTURE §8.1, amended by the lead 2026-10-08).
//
// v1 schema: enum and boolean only. ≤ 6 options per template, camelCase keys,
// every option has a default reproducing the reference design, and an option
// may change HOW something shows, never WHETHER a core feature exists.
//
// The backend keeps a pure-data mirror of every template's options
// (backend/src/services/site-templates/templates/<id>.ts) and is the
// authority for validation; test/site/contract/options.sync.test.ts keeps the
// two copies deep-equal.
//
// Surface rule (replaces the original `followLeagueBackground` standard
// option): every non-classic template declares exactly ONE enum option with
// `role: 'surface'` whose values include 'auto' and whose default is 'auto'.
// The template picks the key and the other values (Arena `canvas`:
// auto|light|night; Huddle `surface`: auto|paper|white|night). 'auto' resolves
// per active sport from the league theme's background tone, so a dark-themed
// league starts in the template's dark look. See `resolveSurface` in
// ./brand.ts and `useLeagueTone()` in ./context.ts.

import type { TemplateText } from './text'

export interface EnumOptionField<T = TemplateText> {
  key: string
  type: 'enum'
  label: T
  help?: T
  default: string
  values: readonly { value: string; label: T }[]
  /** 'surface': the template's light/dark ground choice (exactly one per non-classic template). */
  role?: 'surface'
  /**
   * Surface options only (required there): which value 'auto' resolves to for
   * a light / dark league theme, e.g. { light: 'light', dark: 'night' }.
   */
  autoMap?: { light: string; dark: string }
}

export interface BooleanOptionField<T = TemplateText> {
  key: string
  type: 'boolean'
  label: T
  help?: T
  default: boolean
}

/** `T`: the text type; TemplateText in manifests, string once the admin resolves it (#1063). */
export type OptionField<T = TemplateText> = EnumOptionField<T> | BooleanOptionField<T>
export type OptionsSchema = readonly OptionField[]

/** Typed options for a schema declared `as const`. */
export type OptionsOf<S extends OptionsSchema> = {
  [F in S[number] as F['key']]: F extends { type: 'boolean' } ? boolean
    : F extends { values: readonly { value: infer V }[] } ? V : never
}

/** Raw stored/override option values, as they travel in the tenant payload. */
export type RawOptions = Record<string, string | boolean>

export const MAX_TEMPLATE_OPTIONS = 6
export const OPTION_KEY_PATTERN = /^[a-z][a-zA-Z0-9]*$/
/** The value every surface option must offer and default to. */
export const SURFACE_AUTO = 'auto'

/**
 * Fill defaults and drop unknown or invalid values. The frontend is tolerant
 * (a stale cached tenant, an older payload); the backend is strict.
 */
export function resolveOptions<S extends OptionsSchema>(schema: S, raw: unknown): OptionsOf<S> {
  const src = raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {}
  const out: Record<string, string | boolean> = {}
  for (const field of schema) {
    const v = src[field.key]
    if (field.type === 'boolean') {
      out[field.key] = typeof v === 'boolean' ? v : field.default
    } else {
      out[field.key] = typeof v === 'string' && field.values.some((x) => x.value === v) ? v : field.default
    }
  }
  return out as OptionsOf<S>
}

/** Only the values that differ from the defaults (the stored shape, §8.2). */
export function nonDefaultOptions(schema: OptionsSchema, values: RawOptions): RawOptions {
  const out: RawOptions = {}
  for (const field of schema) {
    if (field.key in values && values[field.key] !== field.default) out[field.key] = values[field.key]
  }
  return out
}

/** The template's surface option, if it declares one. */
export function surfaceOption(schema: OptionsSchema): EnumOptionField | null {
  return (schema.find((f) => f.type === 'enum' && f.role === 'surface') as EnumOptionField | undefined) ?? null
}

/**
 * Problems with a schema, as human-readable strings (empty = valid). Used by
 * the manifest contract test; `requireSurface` is true for every non-classic
 * template.
 */
export function validateOptionsSchema(schema: OptionsSchema, { requireSurface }: { requireSurface: boolean }): string[] {
  const problems: string[] = []
  if (schema.length > MAX_TEMPLATE_OPTIONS) problems.push(`more than ${MAX_TEMPLATE_OPTIONS} options (${schema.length})`)
  const keys = new Set<string>()
  for (const f of schema) {
    if (!OPTION_KEY_PATTERN.test(f.key)) problems.push(`option key "${f.key}" is not camelCase`)
    if (keys.has(f.key)) problems.push(`duplicate option key "${f.key}"`)
    keys.add(f.key)
    if (typeof f.label === 'string' && !f.label.trim()) problems.push(`option "${f.key}" has no label`)
    if (f.type === 'enum') {
      if (f.values.length < 2) problems.push(`enum option "${f.key}" needs ≥ 2 values`)
      const vals = f.values.map((v) => v.value)
      if (new Set(vals).size !== vals.length) problems.push(`enum option "${f.key}" has duplicate values`)
      if (!vals.includes(f.default)) problems.push(`option "${f.key}" default "${f.default}" is not one of its values`)
      if (f.role !== undefined && f.role !== 'surface') problems.push(`option "${f.key}" has unknown role "${String(f.role)}"`)
      if (f.role === 'surface') {
        if (!vals.includes(SURFACE_AUTO)) problems.push(`surface option "${f.key}" must offer "${SURFACE_AUTO}"`)
        if (f.default !== SURFACE_AUTO) problems.push(`surface option "${f.key}" must default to "${SURFACE_AUTO}"`)
        if (!f.autoMap) problems.push(`surface option "${f.key}" needs autoMap { light, dark }`)
        else for (const tone of ['light', 'dark'] as const) {
          const v = f.autoMap[tone]
          if (!vals.includes(v) || v === SURFACE_AUTO) problems.push(`surface option "${f.key}" autoMap.${tone} must be one of its non-auto values`)
        }
      } else if (f.autoMap) {
        problems.push(`option "${f.key}" has autoMap but no role 'surface'`)
      }
    } else if (typeof (f as { default: unknown }).default !== 'boolean') {
      // Runtime guard for hand-written (untyped) schemas.
      problems.push(`boolean option "${(f as OptionField).key}" needs a boolean default`)
    }
  }
  const surfaces = schema.filter((f) => f.type === 'enum' && f.role === 'surface').length
  if (requireSurface && surfaces !== 1) problems.push(`needs exactly one option with role 'surface' (has ${surfaces})`)
  if (!requireSurface && surfaces > 1) problems.push(`at most one option may have role 'surface' (has ${surfaces})`)
  return problems
}
